// src/pages/MatchSessionPlusMinusSelectionPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import Loading from '../components/Loading';
import API_BASE_URL from '../config/baseUrl';
import { toast } from 'react-toastify';
import { formatDisplayUserId } from '../utils/userIdFormat';
import { useGetCompanyReportEventQuery } from '../services/sports/sportsApi';

// Fetch session plus minus data
const fetchSessionPlusMinus = async function(matchId, matchCompleted) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/report/get-session-plus-minus",
      {
        matchId: matchId,
        matchCompleted: matchCompleted,
        oddsAndSessionBoth: true
      },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch data" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch data";
    return { success: false, error: errorMessage };
  }
};

const MatchSessionPlusMinusSelectionPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [matchCompleted, setMatchCompleted] = useState(false);
  
  // Call company report event API
  const { data: companyReportData, isLoading: companyReportLoading, error: companyReportError } = useGetCompanyReportEventQuery({
    eventId: matchId
  });

  console.log("Company Report Event Data:", companyReportData);
  
  const formatClientId = function(userId) {
    return formatDisplayUserId(userId);
  };

  // Fetch data on mount
  useEffect(function() {
    const loadData = async function() {
      setLoading(true);
      setError(null);

      const result = await fetchSessionPlusMinus(matchId, matchCompleted);

      if (result.success) {
        const users = result.data.userDetail || [];
        const sessions = result.data.sessionDetail || [];
        
        setUserData(users);
        setSessionData(sessions);
        
        // Pre-select all users and sessions
        setSelectedUsers(users.map(function(u) { return u.userId; }));
        setSelectedSessions(sessions.map(function(s) { return s.sessionId; }));
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    if (matchId) {
      loadData();
    }
  }, [matchId, matchCompleted]);

  // Handle user checkbox toggle
  

 

  // Handle show button click
 


  // Get company report data
  const reportData = companyReportData?.data || [];
  const summary = companyReportData?.summary || {};
  
  // Get dynamic title based on userType
  const getReportTitle = () => {
    if (reportData.length > 0) {
      const userType = reportData[0].userType;
      const userTypeMapping = {
        'SuperAdmin': 'Super Admin',
        'MiniAdmin': 'Mini Admin',
        'Master': 'Master',
        'Super': 'Super Master',
        'Agent': 'Agent',
        'Client': 'Client'
      };
      return `${userTypeMapping[userType] || userType} Company Report`;
    }
    return 'Company Report';
  };
  
  // Get parent level share column name based on current user type
  const getParentShareColumnName = () => {
    if (reportData.length > 0) {
      const userType = reportData[0].userType;
      const parentMapping = {
        'SuperAdmin': 'Admin Share',
        'MiniAdmin': 'Super Admin Share',
        'Master': 'Mini Admin Share',
        'Super': 'Master Share',
        'Agent': 'Agent Share',
        'Client': 'My Share'
      };
      return parentMapping[userType] || 'My Share';
    }
    return 'My Share';
  };
  
  // Handle user click to navigate to downline
  const handleUserClick = (item) => {
    if (item.userId && item.userType !== 'Client') {
      navigate(`/sports-betting/company-report/${matchId}/${encodeURIComponent(matchName)}/downline/${item.userId}`);
    }
  };

  return (
    <div className="w-full">
      {/* Company Report Table */}
      <div className="bg-[#2c3e3e] text-white px-5 py-3 flex items-center justify-between">
        <div>
          <div className="text-xl font-normal">{getReportTitle()}</div>
          {/* <div className="text-base">{reportMatchName}</div> */}
        </div>
        <button 
          onClick={function() { navigate(-1); }}
          className="bg-blue-500 text-white px-6 py-2 hover:bg-blue-600"
        >
          Back
        </button>
      </div>

      <div className="overflow-x-auto mt-1 py-0">
        <table className="w-full border-collapse text-sm border border-gray-300">
          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="p-2 border border-gray-600 text-left">
                <div className="flex items-center">
                  <button className="mr-2">🔍</button>
                  Code
                </div>
              </th>
              <th className="p-2 border border-gray-600 text-left">Name</th>
              <th className="p-2 border border-gray-600 text-left">Match Amt</th>
              <th className="p-2 border border-gray-600 text-left">Sess Amt</th>
              <th className="p-2 border border-gray-600 text-left">Total</th>
              <th className="p-2 border border-gray-600 text-left">Match Comm+</th>
              <th className="p-2 border border-gray-600 text-left">Sess Comm+</th>
              <th className="p-2 border border-gray-600 text-left">Total Comm</th>
              <th className="p-2 border border-gray-600 text-left">Total Amount</th>
              <th className="p-2 border border-gray-600 text-left">{getParentShareColumnName()}</th>
              <th className="p-2 border border-gray-600 text-left">M.App</th>
              <th className="p-2 border border-gray-600 text-left">Net Amount</th>
            </tr>
          </thead>
          <tbody>
            {companyReportLoading ? (
              <tr>
                <td colSpan="12" className="p-8 text-center">
                  <Loading />
                </td>
              </tr>
            ) : companyReportError ? (
              <tr>
                <td colSpan="12" className="p-4 text-red-500 text-center">
                  {companyReportError.message || "Failed to load data"}
                </td>
              </tr>
            ) : reportData.length === 0 ? (
              <tr>
                <td colSpan="12" className="p-4 text-center text-gray-400">
                  No Data
                </td>
              </tr>
            ) : (
              <>
                {reportData.map((item, index) => (
                  <tr key={index} className="bg-white">
                    <td className="p-2 border border-gray-300">
                      {item.userId && item.userType !== 'Client' ? (
                        <button
                          onClick={() => handleUserClick(item)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        >
                          {item.code}
                        </button>
                      ) : (
                        <span>{item.code}</span>
                      )}
                    </td>
                    <td className="p-2 border border-gray-300">{item.name}</td>
                    <td className={`p-2 border border-gray-300 ${item.matchAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.matchAmt?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.sessAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.sessAmt?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.total?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.matchComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.matchComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.sessComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.sessComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.totalComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.totalComm?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.totalAmount?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.myCompanyShare >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.myShare?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.mApp >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.mApp?.toFixed(2)}
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.netAmount >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                      {item.netAmount?.toFixed(2)}
                    </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-[#d4e157]">
                  <td colSpan="2" className="p-2 border border-gray-300 font-semibold">Total</td>
                  <td className={`p-2 border border-gray-300 ${summary.matchAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.matchAmt?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.sessAmt >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.sessAmt?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.total?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.matchComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.matchComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.sessComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.sessComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.totalComm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalComm?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.totalAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.totalAmount?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.myCompanyShare >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.myShare?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.mApp >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {summary.mApp?.toFixed(2)}
                  </td>
                  <td className={`p-2 border border-gray-300 ${summary.netAmount >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                    {summary.netAmount?.toFixed(2)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

   
    </div>
  );
};

export default MatchSessionPlusMinusSelectionPage;
